import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";

const groupByArt = (causes) => {
  const groups = new Map();
  causes.forEach((cause) => {
    const art = cause.art ?? "Ohne Schadenart";
    groups.set(art, [...(groups.get(art) ?? []), cause]);
  });
  return [...groups];
};

/**
 * The causes claims of a Sparte may name: all (null) or an own selection of
 * internal cause ids. The search narrows the list; Alle/Keine act on the
 * visible causes. With allowUnchanged, undefined leaves each Sparte's setting
 * as it is. The parent keeps the selection non-empty before saving.
 */
export default function ClaimFormSparteClaimCauses({
  sparte,
  causes,
  disabled,
  allowUnchanged = false,
  onChange,
}) {
  const [search, setSearch] = useState("");
  const ownSelection = sparte.claim_causes ?? null;
  const mode =
    allowUnchanged && sparte.claim_causes === undefined
      ? "unchanged"
      : ownSelection
        ? "custom"
        : "default";
  // Ids of a deleted cause are dropped on save, so only catalog causes count.
  const tickedCount = ownSelection
    ? causes.filter((cause) => ownSelection.includes(cause.cause_id)).length
    : 0;

  const needle = search.trim().toLowerCase();
  const visibleCauses = causes.filter((cause) =>
    cause.label.toLowerCase().includes(needle)
  );
  const groups = groupByArt(visibleCauses);
  const showGroupHeaders = new Set(causes.map((cause) => cause.art)).size > 1;

  const handleModeChange = (event) => {
    const modeValues = {
      unchanged: undefined,
      default: null,
      custom: causes.map((cause) => cause.cause_id),
    };
    onChange(modeValues[event.target.value]);
  };

  const setSelection = (ids) => {
    onChange(causes.map((cause) => cause.cause_id).filter((id) => ids.has(id)));
  };

  const handleToggle = (causeId) => {
    const next = new Set(ownSelection);
    if (!next.delete(causeId)) {
      next.add(causeId);
    }
    setSelection(next);
  };

  const handleVisible = (ticked) => {
    const next = new Set(ownSelection);
    visibleCauses.forEach(({ cause_id }) =>
      ticked ? next.add(cause_id) : next.delete(cause_id)
    );
    setSelection(next);
  };

  return (
    <Box>
      <RadioGroup
        row
        aria-label={`Ursachen ${sparte.name}`}
        value={mode}
        onChange={handleModeChange}
      >
        {allowUnchanged && (
          <FormControlLabel
            value="unchanged"
            control={<Radio size="small" disabled={disabled} />}
            label="Unverändert lassen"
          />
        )}
        <FormControlLabel
          value="default"
          control={<Radio size="small" disabled={disabled} />}
          label="Standard (alle Ursachen)"
        />
        <FormControlLabel
          value="custom"
          control={<Radio size="small" disabled={disabled || !causes.length} />}
          label="Eigene Auswahl"
        />
      </RadioGroup>
      {ownSelection && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              size="small"
              label="Ursachen suchen"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Button
              size="small"
              disabled={disabled}
              onClick={() => handleVisible(true)}
            >
              Alle
            </Button>
            <Button
              size="small"
              disabled={disabled}
              onClick={() => handleVisible(false)}
            >
              Keine
            </Button>
            <Typography variant="body2" color="text.secondary" ml="auto">
              {tickedCount} von {causes.length} ausgewählt
            </Typography>
          </Box>
          {tickedCount === 0 && (
            <Typography variant="caption" color="error">
              Mindestens eine Ursache auswählen
            </Typography>
          )}
          <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
            {groups.map(([art, artCauses]) => (
              <FormGroup
                key={art}
                role="group"
                aria-label={art}
                sx={{ display: "block", mb: 1 }}
              >
                {showGroupHeaders && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {art}
                  </Typography>
                )}
                <Box sx={{ columnWidth: 220 }}>
                  {artCauses.map((cause) => (
                    <FormControlLabel
                      key={cause.cause_id}
                      sx={{ display: "flex", breakInside: "avoid" }}
                      label={cause.label}
                      slotProps={{ typography: { variant: "body2" } }}
                      control={
                        <Checkbox
                          size="small"
                          checked={ownSelection.includes(cause.cause_id)}
                          disabled={disabled}
                          onChange={() => handleToggle(cause.cause_id)}
                        />
                      }
                    />
                  ))}
                </Box>
              </FormGroup>
            ))}
            {!groups.length && (
              <Typography variant="body2" color="text.secondary">
                Keine Ursachen gefunden
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

ClaimFormSparteClaimCauses.propTypes = {
  sparte: PropTypes.shape({
    name: PropTypes.string.isRequired,
    claim_causes: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  causes: PropTypes.arrayOf(
    PropTypes.shape({
      cause_id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      art: PropTypes.string,
    })
  ).isRequired,
  disabled: PropTypes.bool,
  allowUnchanged: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};
